import { Space, type TableColumnsType, Tag } from 'antd'

import { FormattedDate } from '@/components/common/FormattedDate'
import { MonoId } from '@/components/common/MonoId'
import { StatusTag } from '@/components/common/StatusTag'
import { UserEmailLink } from '@/components/common/UserEmailLink'

import { providerName } from '../format'
import type { GpuInstance } from '../hooks/useGpuOperations'

export const INSTANCE_STATUSES = ['INITIALIZED', 'ACTIVE', 'ENDED', 'FAILED']
export const WORKLOADS = ['CONTAINER', 'VM', 'BAREMETAL']

const STATUS_COLORS = { INITIALIZED: 'orange', ENDED: 'default' }

export const DATE_FMT = 'MMM d, yyyy HH:mm'

export function InstanceStatus({ status }: { status: string }) {
  return <StatusTag status={status} colorMap={STATUS_COLORS} />
}

export function ReservationTags({
  reservation,
}: {
  reservation: GpuInstance['reservation']
}) {
  if (!reservation) return <>{'—'}</>
  return (
    <Space size={4} wrap>
      <StatusTag status={reservation.status} />
      {reservation.held_at && <Tag color="red">Report held</Tag>}
      {reservation.refusal && <Tag color="red">{reservation.refusal}</Tag>}
    </Space>
  )
}

export const instanceColumns: TableColumnsType<GpuInstance> = [
  {
    title: 'Instance',
    dataIndex: 'name',
    render: (name: string, instance) => (
      <Space direction="vertical" size={0}>
        {name}
        <MonoId id={instance.id} />
      </Space>
    ),
  },
  {
    title: 'User',
    dataIndex: 'user_id',
    width: 220,
    render: (userId: string) => <UserEmailLink userId={userId} />,
  },
  {
    title: 'Provider',
    dataIndex: 'provider',
    render: (key: string) => providerName(key),
  },
  { title: 'Workload', dataIndex: 'workload' },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: string) => <InstanceStatus status={status} />,
  },
  {
    title: 'Lease',
    dataIndex: 'lease',
    render: (lease: GpuInstance['lease']) =>
      lease ? <StatusTag status={lease.status} /> : '—',
  },
  {
    title: 'Reservation',
    dataIndex: 'reservation',
    render: (reservation: GpuInstance['reservation']) => (
      <ReservationTags reservation={reservation} />
    ),
  },
  {
    title: 'Open tasks',
    dataIndex: 'open_tasks',
    align: 'right',
    render: (tasks: GpuInstance['open_tasks']) => tasks.length,
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    render: (date: string) => <FormattedDate date={date} fmt={DATE_FMT} />,
  },
]
